// src/pages/Platform/providerReassignRules.ts — the decisions behind Number Ownership's
// PROVIDER view (ProviderNumberOwnership.tsx), kept out of the component so they can be tested.
import type {
  ProviderHeldNumber, ProviderPush, ProviderTarget,
} from 'api/services/providerNumbers.service';

/**
 * Roles that open the provider view. Both spellings: the enum is mid-rename, every real
 * provider admin holds SUPER_ADMIN or SERVICE_ADMIN, and there is no role hierarchy.
 */
export const PROVIDER_ROLES = ['super_admin', 'service_admin', 'provider_owner', 'provider_admin'];

export const isProviderRole = (role: string | null | undefined): boolean =>
  PROVIDER_ROLES.includes((role || '').toLowerCase());

/** Stated on the page and in every confirmation. */
export const MOVE_COPY = 'Moves take effect for billing immediately; the switch updates within ~60 s.';

/** Organizations this number may move to: assignable, and not its current holder. */
export const reassignTargets = (targets: ProviderTarget[], n: ProviderHeldNumber | null | undefined): ProviderTarget[] =>
  targets.filter(t => t.assignable && t.id !== n?.holder_organization_id);

/** Why "Reassign" to this target is unavailable, or null when it is allowed. */
export const reassignBlock = (n: ProviderHeldNumber | null | undefined, targetId: string,
                              targets: ProviderTarget[]): string | null => {
  if (!n) return 'Pick a number.';
  if (!targetId) return 'Pick an organization.';
  if (targetId === n.holder_organization_id) return `${n.holder_name} already holds ${n.e164}.`;
  const t = targets.find(x => x.id === targetId);
  if (!t) return 'That organization is not part of your provider.';
  if (!t.assignable) return t.reason || `${t.name} cannot hold a number.`;
  return null;
};

/** "Return to provider" is pointless when the provider org itself already holds it. */
export const canReturn = (n: ProviderHeldNumber): boolean => !n.held_by_provider;

export type Tone = 'success' | 'neutral' | 'warning' | 'danger';

/** How one move's switch result reads on the page. */
export const pushSummary = (p: ProviderPush | null | undefined): { label: string; tone: Tone; detail: string } => {
  if (!p) return { label: 'Not reported', tone: 'warning', detail: 'The server did not report the switch result.' };
  const detail = p.reason || '';
  switch (p.outcome) {
    case 'OK':
      return { label: `Switch: relabelled${p.newLabel ? ` to ${p.newLabel}` : ''}`, tone: 'success', detail };
    case 'NO_CHANGE':
      return { label: 'Switch: no change needed', tone: 'neutral', detail };
    case 'BLOCKED':
      return {
        label: 'Switch: BLOCKED',
        tone: 'warning',
        detail: `${detail} Billing has moved; the switch label has not. The platform admin can fix the billing patterns, then push.`,
      };
    case 'FAILED':
      return { label: 'Switch: write failed', tone: 'danger', detail: `${detail} Billing has moved; ask the platform admin to push again.` };
    case 'PREVIEW':
      return { label: 'Switch: preview only', tone: 'neutral', detail: `${detail} (switch push is in preview mode on the server.)` };
    case 'OFF':
      return { label: 'Switch: push is off', tone: 'neutral', detail };
    default:
      return { label: `Switch: ${String(p.outcome)}`, tone: 'neutral', detail };
  }
};

export const tonePill = (t: Tone): string =>
  t === 'success' ? 'tl-pill tl-pill-success' : t === 'warning' ? 'tl-pill tl-pill-amber'
    : t === 'danger' ? 'tl-pill tl-pill-coral' : 'tl-pill';

/** Search by number or holder, optionally one holder. */
export const filterHeld = (rows: ProviderHeldNumber[], query: string, holderId: string): ProviderHeldNumber[] => {
  const q = query.trim().toLowerCase();
  // "0091…" and "+91…" are the same number: drop the international 00 prefix.
  const digits = q.replace(/\D/g, '').replace(/^00/, '');
  return rows.filter(r => {
    if (holderId && r.holder_organization_id !== holderId) return false;
    if (!q) return true;
    if (digits && r.e164.replace(/\D/g, '').includes(digits)) return true;
    return (r.holder_name || '').toLowerCase().includes(q) || (r.business_id || '').toLowerCase().includes(q);
  });
};
